import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../store";
import { getToken } from "../services/auth";
import { CONTEST_MANAGEMENT_BASE, API_BASE_URL } from "../services/apiConstant";
import dayjs from "dayjs";
import { decryptMessage } from "../utils/decrypt";
import TimeZone from "../services/timeZone";
// ---- Types ----
export interface ContestForm {
  title: string;
  startDate?: string;
  endDate?: string;
  imageCount: number;
  description?: string;
  termsAndCondition?: string;
  status?: number;
  contestImagesID?: string[];
}

interface ContestState {
  form: ContestForm;
  loading: boolean;
  publishLoading: boolean;
  draftLoading: boolean;
  uploadingImages: boolean;
  success: boolean;
  error?: string | null;
  imageUploadError?: string | null;
  contestId?: string | null;
}

// ---- Initial State ----
const initialState: ContestState = {
  form: {
    title: "",
    startDate: undefined,
    endDate: undefined,
    imageCount: 1,
    description: "",
    termsAndCondition: "",
  },
  loading: false,
  publishLoading: false,
  draftLoading: false,
  uploadingImages: false,
  success: false,
  error: null,
  imageUploadError: null,
  contestId: null,
};

// ---- Async Thunks ----

// Fetch contest by ID
export const getContestById = createAsyncThunk<
  ContestForm,
  string,
  { rejectValue: string }
>("contest/getContestById", async (id, { rejectWithValue }) => {
  try {

    const token = getToken();
    if (!token) throw new Error("No token found");

    const endpoint = `${API_BASE_URL + CONTEST_MANAGEMENT_BASE}/${id}`;

    const res = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const decryptedData = await decryptMessage(res.data.Data, token);
    // Map API response into our form shape including contestImagesID as URLs
    const data: any = decryptedData;
    const mapped: ContestForm = {
      title: data.title,
      startDate: data.StartDate,
      endDate: data.EndDate,
      imageCount: Number(data.imageCount) || 0,   // 👈 map
      description: data.description || "",
      termsAndCondition: data.termsAndCondition || "",
      status: data.status,
      contestImagesID: (data.contestManagementImageResponses || []).map((img: any) => img.id).filter(Boolean),
    };
    return mapped;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || "Failed to fetch contest");
  }
});

// Update existing contest
export const updateContest = createAsyncThunk<
  ContestForm,
  { id: string; data: ContestForm },
  { rejectValue: string }
>("contest/updateContest", async ({ id, data }, { rejectWithValue }) => {
  try {

    const token = getToken();
    if (!token) return rejectWithValue("Unauthorized: No token provided");

    // Helper to safely parse various incoming date formats to YYYY-MM-DD
    const toYmd = (value?: string) => {
      if (!value) return undefined as unknown as string | undefined;
      const known = ["YYYY-MM-DD", "MM-DD-YYYY", "ddd, DD-MM-YYYY", "DD-MM-YYYY"] as const;
      for (const fmt of known) {
        const d = dayjs(value, fmt, true);
        if (d.isValid()) return d.format("YYYY-MM-DD");
      }
      const iso = dayjs(value);
      return iso.isValid() ? iso.format("YYYY-MM-DD") : undefined;
    };

    // Build payload according to API contract
    const payload = {
      id,
      title: data.title || null,
      startDate: toYmd(data.startDate) || null,
      endDate: toYmd(data.endDate) || null,
      imageCount: Number(data.imageCount) || 0,
      termsAndCondition: data.termsAndCondition || null,
      status: typeof data.status === "number" ? data.status : 0,
      description: data.description || null,
      contestImagesID: Array.isArray(data.contestImagesID) ? data.contestImagesID : [],
      timezone: TimeZone.getFriendlyTimeZone()
    } as any;

    const url = API_BASE_URL + CONTEST_MANAGEMENT_BASE + "/UpdateContestfromWeb";
    const res = await axios.put(
      url,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return res.data;

  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || "Failed to update contest");
  }
});

// Save new contest
export const saveContest = createAsyncThunk<
  { id: string },
  { data: ContestForm; status: 0 | 1 },
  { rejectValue: string }
>("contest/saveContest", async ({ data }, { rejectWithValue }) => {
  try {
    const token = getToken();
    if (!token) return rejectWithValue("Unauthorized: No token provided");

    const res = await axios.post(
      API_BASE_URL + CONTEST_MANAGEMENT_BASE,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return res.data;
  } catch (err: any) {
    console.error("SaveContest API Error:", err.response);

    // ✅ Improved error extraction logic
    const backendMessage =
      err?.response?.data?.Message || // ✅ backend PascalCase key
      err?.response?.data?.message || // fallback lowercase key
      err?.response?.data?.Detail ||  // some APIs include error info here
      err?.message ||                 // fallback JS error message
      "Failed to create contest.";    // default fallback

    return rejectWithValue(backendMessage);
  }
});

// Upload contest images
export const uploadContestImages = createAsyncThunk<
  void,
  { contestId: string; images: string[] },
  { rejectValue: string; state: RootState }
>(
  "contest/uploadContestImages",
  async ({ contestId, images }, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) return rejectWithValue("Unauthorized: No token provided");

      for (let index = 0; index < images.length; index++) {
        const img = images[index];

        // Convert base64 → Blob
        const byteString = atob(img.split(",")[1]);
        const mimeString = img.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });

        // Build FormData
        const formData = new FormData();
        formData.append("ContestFile", blob, `contest_${contestId}_${index}.png`);
        formData.append("ContestID", contestId);

        // Upload
        await axios.post(
          `${API_BASE_URL + CONTEST_MANAGEMENT_BASE}/ContestManagementImages`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to upload contest images");
    }
  }
);

// ---- Slice ----
const contestSlice = createSlice({
  name: "contest",
  initialState,
  reducers: {
    updateForm: (state, action: PayloadAction<Partial<ContestForm>>) => {
      state.form = { ...state.form, ...action.payload };
    },
    resetForm: (state) => {
      // state.form = initialState.form;
      state.form = {
        title: "",
        startDate: "",
        endDate: "",
        imageCount: 0,
        description: "",
        termsAndCondition: "",
        //images: [],
      };
      state.error = null;
      state.success = false;
      state.contestId = null;
      state.uploadingImages = false;
      state.imageUploadError = null;
    },
  },
  extraReducers: (builder) => {
    // getContestById
    builder
      .addCase(getContestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContestById.fulfilled, (state, action) => {
        state.loading = false;
        state.form = action.payload;

      })
      .addCase(getContestById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch contest";
      });

    // updateContest
    builder
      .addCase(updateContest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContest.fulfilled, (state, action) => {
        state.loading = false;
        const data: any = action.payload || {};
        state.form = {
          title: data.title || "",
          startDate: data.startDate || data.StartDate || undefined,
          endDate: data.endDate || data.EndDate || undefined,
          imageCount: (typeof data.imageCount === "number" ? data.imageCount : data.imageCount) ?? 0,
          description: data.description || "",
          termsAndCondition: data.termsAndCondition || "",
          status: typeof data.status === "number" ? data.status : 0,
          contestImagesID: Array.isArray(data.contestImagesID)
            ? data.contestImagesID
            : Array.isArray(data.contestManagementImageResponses)
              ? data.contestManagementImageResponses.map((img: any) => img.fileUrl).filter(Boolean)
              : [],
        };
        state.success = true;
      })
      .addCase(updateContest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update contest";
      });

    // saveContest
    builder
      .addCase(saveContest.pending, (state, action) => {
        if (action.meta.arg.status === 1) state.publishLoading = true;
        if (action.meta.arg.status === 0) state.draftLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(saveContest.fulfilled, (state, action) => {
        state.publishLoading = false;
        state.draftLoading = false;
        state.success = true;
        state.contestId = action.payload?.id || null;
      })
      .addCase(saveContest.rejected, (state, action) => {
        state.publishLoading = false;
        state.draftLoading = false;
        state.error =
          (action.payload as string) || action.error.message || "Failed to save contest";
      });

    // uploadContestImages
    builder
      .addCase(uploadContestImages.pending, (state) => {
        state.uploadingImages = true;
        state.imageUploadError = null;
      })
      .addCase(uploadContestImages.fulfilled, (state) => {
        state.uploadingImages = false;
      })
      .addCase(uploadContestImages.rejected, (state, action) => {
        state.uploadingImages = false;
        state.imageUploadError =
          (action.payload as string) || action.error.message || "Failed to upload images";
      });
  },
});

export const { updateForm, resetForm } = contestSlice.actions;
export default contestSlice.reducer;